import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { CurrentUser } from '@ecc/shared';
import { ApiError, api } from '../api/client';

/**
 * Stato di autenticazione dell'operatore.
 *
 * La verità sta nel cookie di sessione lato server: qui si conserva solo il
 * risultato dell'ultima verifica, mai una credenziale. Un ricaricamento della
 * pagina interroga di nuovo `/api/auth/me`.
 */

type AuthState =
  | { status: 'loading' }
  | { status: 'authenticated'; user: CurrentUser }
  | { status: 'anonymous' };

type AuthValue = AuthState & {
  readonly login: (email: string, password: string) => Promise<void>;
  readonly logout: () => Promise<void>;
};

const AuthContext = createContext<AuthValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({ status: 'loading' });

  const refresh = useCallback(async () => {
    try {
      const user = await api.me();
      setState({ status: 'authenticated', user });
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) {
        setState({ status: 'anonymous' });
        return;
      }
      // Errore di rete: non è una prova di mancata autenticazione, ma senza
      // conferma dal server l'utente resta trattato come anonimo.
      setState({ status: 'anonymous' });
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const login = useCallback(
    async (email: string, password: string) => {
      await api.login(email, password);
      await refresh();
    },
    [refresh],
  );

  const logout = useCallback(async () => {
    await api.logout();
    setState({ status: 'anonymous' });
  }, []);

  const value = useMemo<AuthValue>(() => ({ ...state, login, logout }), [state, login, logout]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthValue {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth richiede AuthProvider.');
  return context;
}
